/**
 * Учебный источник данных (E4.5): маршрутизатор запросов HTTP-клиента в учебном режиме.
 * Каждый запрос страницы к данным рабочего пространства сопоставляется с предметом учебного
 * представления и уходит в учебный фасад экземпляра; ответ имеет форму рабочего ответа, поэтому
 * страницы, хуки и прямые вызовы API работают без учебных вариантов. Запрос, которого нет в
 * таблице, отклоняется без сети. Изменять можно только две вещи -- разбор события и учебную
 * политику доступа; они становятся учебными командами с версией экземпляра.
 */

import i18n from '@/utils/i18n';
import {
	ApiError,
	trainingUnavailableError,
	type RequestRouter,
	type RoutedRequest,
} from '@/api/client';
import {
	getTrainingInstance,
	getTrainingView,
	sendTrainingCommand,
	type AccessPolicyDraftInput,
	type TrainingCommand,
	type TrainingViewKind,
} from '@/api/training';
import type { WorkflowPatch } from '@/api/workspaceTypes';
import type { WorkspaceDataSource } from '@/features/workspace/liveDataSource';
import { createQueryClient } from '@/lib/queryClient';
import { demoDiagnosticRead } from './llmGateDiagnostics';

/** Строка таблицы чтения: путь рабочего запроса → экран и предмет учебного фасада */
interface ReadRoute {
	pattern: RegExp;
	view: TrainingViewKind;
	subject: string;
	/** Параметры пути: группы регулярного выражения → имена параметров фасада */
	path?: string[];
}

const ID = '([^/]+)';

/** Чтения десяти экранов; порядок важен: частные пути раньше общих */
export const READ_ROUTES: readonly ReadRoute[] = [
	{
		pattern: /^\/v1\/integration-targets\/([^/]+)\/deliveries\/page$/,
		view: 'integrations',
		subject: 'target_deliveries',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/integration-targets\/([^/]+)\/audit\/page$/,
		view: 'integrations',
		subject: 'target_audit',
		path: ['id'],
	},
	{ pattern: /^\/v2\/assets$/, view: 'services', subject: 'assets' },
	{ pattern: /^\/v2\/assets-extended$/, view: 'services', subject: 'assets_extended' },
	{ pattern: /^\/v2\/assets\/([^/]+)$/, view: 'services', subject: 'asset', path: ['id'] },
	{ pattern: /^\/v2\/assets\/([^/]+)\/ips$/, view: 'services', subject: 'asset_ips', path: ['id'] },
	{
		pattern: /^\/v2\/assets\/([^/]+)\/user-agents$/,
		view: 'services',
		subject: 'asset_user_agents',
		path: ['id'],
	},
	{
		pattern: /^\/v2\/resources\/([^/]+)\/dataflows\/([^/]+)\/(outgoing|incoming)$/,
		view: 'services',
		subject: 'flow_detail',
		path: ['id', 'peer', 'direction'],
	},
	{
		pattern: /^\/v2\/assets\/([^/]+)\/dataflows$/,
		view: 'services',
		subject: 'asset_dataflows',
		path: ['id'],
	},
	{
		pattern: /^\/v2\/assets\/([^/]+)\/datastorages$/,
		view: 'services',
		subject: 'asset_datastorages',
		path: ['id'],
	},
	{ pattern: /^\/v2\/trafficPaths$/, view: 'endpoints', subject: 'endpoints' },
	{ pattern: /^\/v2\/endpoints\/summary$/, view: 'endpoints', subject: 'endpoint_summary' },
	{ pattern: /^\/v2\/endpoints\/([^/]+)$/, view: 'endpoints', subject: 'endpoint', path: ['id'] },
	{ pattern: /^\/v2\/sample$/, view: 'endpoints', subject: 'endpoint_sample' },
	{
		pattern: /^\/v3\/endpoints\/([^/]+)\/series$/,
		view: 'endpoints',
		subject: 'endpoint_series',
		path: ['id'],
	},
	{ pattern: /^\/v3\/sd-mark$/, view: 'endpoints', subject: 'endpoint_marks' },
	{
		pattern: /^\/v2\/log-events\/([^/]+)\/sample$/,
		view: 'event',
		subject: 'event_sample',
		path: ['id'],
	},
	{ pattern: /^\/v3\/data-types\/usage$/, view: 'data-types', subject: 'usage' },
	{
		pattern: /^\/v3\/data-types\/([^/]+)\/occurrences$/,
		view: 'data-types',
		subject: 'occurrences',
		path: ['id'],
	},
	{
		pattern: /^\/v3\/data-types\/([^/]+)\/resources$/,
		view: 'data-types',
		subject: 'type_resources',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/data-types\/([^/]+)\/rules$/,
		view: 'data-types',
		subject: 'type_rules',
		path: ['id'],
	},
	{ pattern: /^\/v2\/groups\/([^/]+)$/, view: 'groups', subject: 'group', path: ['id'] },
	{
		pattern: /^\/v2\/groups\/([^/]+)\/counters$/,
		view: 'groups',
		subject: 'group_counters',
		path: ['id'],
	},
	{
		pattern: /^\/v2\/groups\/([^/]+)\/dataflows$/,
		view: 'groups',
		subject: 'group_dataflows',
		path: ['id'],
	},
	{
		pattern: /^\/v3\/groups\/([^/]+)\/members$/,
		view: 'groups',
		subject: 'group_members',
		path: ['id'],
	},
	{
		pattern: /^\/v3\/groups\/([^/]+)\/members\/explicit$/,
		view: 'groups',
		subject: 'group_explicit_members',
		path: ['id'],
	},
	{ pattern: /^\/v1\/attack\/clusters$/, view: 'attack', subject: 'attack_clusters' },
	{ pattern: /^\/v1\/attack\/stats$/, view: 'attack', subject: 'attack_stats' },
	{ pattern: /^\/v1\/attack\/paths$/, view: 'attack', subject: 'attack_paths' },
	{
		pattern: /^\/v1\/attack\/paths\/([^/]+)$/,
		view: 'attack',
		subject: 'attack_path',
		path: ['id'],
	},
	{ pattern: /^\/v1\/attack\/graph$/, view: 'attack', subject: 'attack_graph' },
	{
		pattern: /^\/v1\/attack\/nodes\/([^/]+)$/,
		view: 'attack',
		subject: 'attack_node',
		path: ['id'],
	},
	{ pattern: /^\/v2\/ai-chats$/, view: 'copilot', subject: 'chats' },
	{ pattern: /^\/v2\/ai-chats\/([^/]+)$/, view: 'copilot', subject: 'chat', path: ['id'] },
	{ pattern: /^\/v2\/copilot\/status$/, view: 'copilot', subject: 'copilot_status' },
	{ pattern: /^\/v2\/copilot\/settings$/, view: 'copilot', subject: 'copilot_settings' },
	{
		pattern: /^\/v3\/workspace\/resources\/([^/]+)\/([^/]+)\/access$/,
		view: 'resource',
		subject: 'resource_access',
		path: ['kind', 'id'],
	},
	{ pattern: /^\/v1\/sql-db\/instances$/, view: 'storage', subject: 'sql_instances' },
	{ pattern: /^\/v1\/sql-db\/databases$/, view: 'storage', subject: 'sql_databases' },
	{
		pattern: /^\/v1\/sql-db\/databases\/([^/]+)$/,
		view: 'storage',
		subject: 'sql_database',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/sql-db\/databases\/([^/]+)\/roles$/,
		view: 'storage',
		subject: 'sql_roles',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/sql-db\/databases\/([^/]+)\/identities$/,
		view: 'storage',
		subject: 'sql_identities',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/sql-db\/databases\/([^/]+)\/identity$/,
		view: 'storage',
		subject: 'sql_identity',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/sql-db\/tables\/([^/]+)\/identities$/,
		view: 'storage',
		subject: 'sql_table_identities',
		path: ['id'],
	},
	{ pattern: /^\/v1\/sql-db\/tables$/, view: 'storage', subject: 'sql_tables' },
	{
		pattern: /^\/v1\/sql-db\/samples\/([^/]+)$/,
		view: 'storage',
		subject: 'sql_sample',
		path: ['id'],
	},
	{ pattern: /^\/v1\/sql-db\/findings$/, view: 'storage', subject: 'sql_findings' },
	{ pattern: /^\/v1\/nosql-db\/instances$/, view: 'storage', subject: 'nosql_instances' },
	{ pattern: /^\/v1\/nosql-db\/databases$/, view: 'storage', subject: 'nosql_databases' },
	{
		pattern: /^\/v1\/nosql-db\/databases\/([^/]+)$/,
		view: 'storage',
		subject: 'nosql_database',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/nosql-db\/databases\/([^/]+)\/collections$/,
		view: 'storage',
		subject: 'nosql_collections',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/nosql-db\/collections\/([^/]+)$/,
		view: 'storage',
		subject: 'nosql_collection',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/nosql-db\/collections\/([^/]+)\/schemas$/,
		view: 'storage',
		subject: 'nosql_schemas',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/nosql-db\/schemas\/([^/]+)$/,
		view: 'storage',
		subject: 'nosql_schema',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/nosql-db\/schemas\/([^/]+)\/samples$/,
		view: 'storage',
		subject: 'nosql_samples',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/nosql-db\/samples\/([^/]+)$/,
		view: 'storage',
		subject: 'nosql_sample',
		path: ['id'],
	},
	{ pattern: /^\/v1\/s3\/objectStores$/, view: 'storage', subject: 's3_buckets' },
	{
		pattern: /^\/v1\/s3\/objectStores\/([^/]+)$/,
		view: 'storage',
		subject: 's3_bucket',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/s3\/objectStores\/([^/]+)\/status$/,
		view: 'storage',
		subject: 's3_status',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/s3\/objectStores\/([^/]+)\/top-clusters$/,
		view: 'storage',
		subject: 's3_clusters',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/s3\/objectStores\/([^/]+)\/sub-clusters\/([^/]+)$/,
		view: 'storage',
		subject: 's3_subclusters',
		path: ['id', 'cluster_id'],
	},
	{
		pattern: /^\/v1\/s3\/clusters\/([^/]+)$/,
		view: 'storage',
		subject: 's3_cluster',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/s3\/object-samples\/([^/]+)$/,
		view: 'storage',
		subject: 's3_sample',
		path: ['id'],
	},
	{ pattern: /^\/v1\/kafka\/instances$/, view: 'storage', subject: 'kafka_instances' },
	{
		pattern: /^\/v1\/kafka\/instances\/([^/]+)$/,
		view: 'storage',
		subject: 'kafka_instance',
		path: ['id'],
	},
	{ pattern: /^\/v1\/kafka\/topics$/, view: 'storage', subject: 'kafka_topics' },
	{
		pattern: /^\/v1\/kafka\/topics\/([^/]+)$/,
		view: 'storage',
		subject: 'kafka_topic',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/kafka\/topics\/([^/]+)\/consumer_groups_members$/,
		view: 'storage',
		subject: 'kafka_consumers',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/kafka\/topics\/([^/]+)\/status$/,
		view: 'storage',
		subject: 'kafka_status',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/kafka\/topics\/([^/]+)\/schemas$/,
		view: 'storage',
		subject: 'kafka_schemas',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/kafka\/schemas\/([^/]+)$/,
		view: 'storage',
		subject: 'kafka_schema',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/kafka\/schemas\/([^/]+)\/samples$/,
		view: 'storage',
		subject: 'kafka_samples',
		path: ['id'],
	},
	{
		pattern: /^\/v1\/kafka\/samples\/([^/]+)$/,
		view: 'storage',
		subject: 'kafka_sample',
		path: ['id'],
	},
	{
		pattern: /^\/v2\/databases\/([^/]+)\/dataflows$/,
		view: 'storage',
		subject: 'storage_flows',
		path: ['id'],
	},
	{
		pattern: /^\/v2\/nosql-databases\/([^/]+)\/dataflows$/,
		view: 'storage',
		subject: 'storage_flows',
		path: ['id'],
	},
	{
		pattern: /^\/v2\/objectStores\/([^/]+)\/dataflows$/,
		view: 'storage',
		subject: 'storage_flows',
		path: ['id'],
	},
	{
		pattern: /^\/v2\/kafkas\/([^/]+)\/dataflows$/,
		view: 'storage',
		subject: 'storage_flows',
		path: ['id'],
	},
	{ pattern: /^\/v1\/data-types$/, view: 'overview', subject: 'data_types' },
	{ pattern: /^\/v2\/collected_group\/counters$/, view: 'overview', subject: 'collected_counters' },
	{ pattern: /^\/v3\/workspace\/summary$/, view: 'overview', subject: 'summary' },
	{ pattern: /^\/v1\/recent-activity$/, view: 'overview', subject: 'recent_activity' },
	{
		pattern: /^\/v2\/discovered-data-types\/groups$/,
		view: 'overview',
		subject: 'data_type_groups',
	},
	{ pattern: /^\/v2\/data-distribution-regions$/, view: 'overview', subject: 'regions' },
	{ pattern: /^\/v2\/data-stores-statistic$/, view: 'overview', subject: 'stores_stats' },
	{
		pattern: /^\/v2\/policy-log-events\/categories$/,
		view: 'overview',
		subject: 'policy_categories',
	},
	{ pattern: /^\/v2\/policy-log-events\/counters$/, view: 'overview', subject: 'policy_counters' },
	{
		pattern: /^\/v3\/workspace\/resources\/facets$/,
		view: 'resources',
		subject: 'resource_facets',
	},
	{ pattern: /^\/v3\/workspace\/resources$/, view: 'resources', subject: 'resources' },
	{
		pattern: new RegExp(`^/v3/workspace/resources/${ID}/${ID}$`),
		view: 'resource',
		subject: 'resource',
		path: ['kind', 'id'],
	},
	{ pattern: /^\/v3\/data-map$/, view: 'map', subject: 'data_map' },
	{ pattern: /^\/v2\/discovery-status$/, view: 'map', subject: 'discovery_status' },
	{ pattern: /^\/v2\/groups$/, view: 'map', subject: 'groups' },
	{ pattern: /^\/v2\/log-events\/statistic$/, view: 'event', subject: 'event_statistic' },
	{ pattern: /^\/v2\/log-events\/assignees$/, view: 'event', subject: 'event_assignees' },
	{
		pattern: new RegExp(`^/v2/log-events/${ID}/workflow/history$`),
		view: 'event',
		subject: 'event_history',
		path: ['id'],
	},
	{
		pattern: new RegExp(`^/v2/log-events/${ID}/workflow$`),
		view: 'event',
		subject: 'event_workflow',
		path: ['id'],
	},
	{ pattern: new RegExp(`^/v2/log-events/${ID}$`), view: 'event', subject: 'event', path: ['id'] },
	{ pattern: /^\/v2\/log-events$/, view: 'event', subject: 'events' },
	{ pattern: /^\/v2\/policies$/, view: 'policies', subject: 'policies' },
	{
		pattern: new RegExp(`^/v2/policies/${ID}$`),
		view: 'policies',
		subject: 'policy',
		path: ['id'],
	},
	{ pattern: /^\/v1\/llm-gate\/routes$/, view: 'llm', subject: 'llm_routes' },
	{
		pattern: new RegExp(`^/v1/llm-gate/routes/${ID}$`),
		view: 'llm',
		subject: 'llm_route',
		path: ['id'],
	},
	{ pattern: /^\/v1\/llm-gate\/policies$/, view: 'llm', subject: 'llm_policies' },
	{
		pattern: new RegExp(`^/v1/llm-gate/policies/${ID}$`),
		view: 'llm',
		subject: 'llm_policy',
		path: ['id'],
	},
	{ pattern: /^\/v1\/llm-gate\/instances$/, view: 'llm', subject: 'llm_instances' },
	{ pattern: /^\/v1\/llm-gate\/summary$/, view: 'llm', subject: 'llm_summary' },
	{ pattern: /^\/v1\/llm-gate\/events$/, view: 'llm', subject: 'llm_events' },
	{
		pattern: new RegExp(`^/v1/llm-gate/events/${ID}$`),
		view: 'llm',
		subject: 'llm_event',
		path: ['id'],
	},
	{ pattern: /^\/v1\/sensors\/dim$/, view: 'sensors', subject: 'dim_sensors' },
	{ pattern: /^\/v1\/sensors\/dar$/, view: 'sensors', subject: 'dar_sensors' },
	{ pattern: /^\/v1\/sensors\/llmgate$/, view: 'sensors', subject: 'llmgate_tokens' },
	{ pattern: /^\/v3\/sensors\/summary$/, view: 'sensors', subject: 'sensor_summary' },
	{ pattern: /^\/v1\/cluster-geo-locations\/regions$/, view: 'sensors', subject: 'geo_regions' },
	{ pattern: /^\/v1\/cluster-geo-locations$/, view: 'sensors', subject: 'geo_locations' },
	{ pattern: /^\/v1\/discovery\/account\/aws$/, view: 'sensors', subject: 'aws_accounts' },
	{
		pattern: /^\/v1\/integration-targets\/profiles$/,
		view: 'integrations',
		subject: 'target_profiles',
	},
	{ pattern: /^\/v1\/integration-targets\/refs$/, view: 'integrations', subject: 'target_refs' },
	{
		pattern: new RegExp(`^/v1/integration-targets/${ID}/backfill-horizon$`),
		view: 'integrations',
		subject: 'target_backfill_horizon',
		path: ['id'],
	},
	{
		pattern: new RegExp(`^/v1/integration-targets/${ID}/(deliveries|audit)/page$`),
		view: 'integrations',
		subject: 'target_activity',
		path: ['id', 'tab'],
	},
	{
		pattern: new RegExp(`^/v1/integration-targets/${ID}/([a-z-]+)$`),
		view: 'integrations',
		subject: 'target_activity',
		path: ['id', 'tab'],
	},
	{
		pattern: new RegExp(`^/v1/integration-targets/${ID}$`),
		view: 'integrations',
		subject: 'target',
		path: ['id'],
	},
	{ pattern: /^\/v1\/integration-targets$/, view: 'integrations', subject: 'targets' },
	{ pattern: /^\/v1\/reports\/templates$/, view: 'reports', subject: 'report_templates' },
	{
		pattern: new RegExp(`^/v1/reports/${ID}$`),
		view: 'reports',
		subject: 'report',
		path: ['id'],
	},
	{ pattern: /^\/v1\/reports$/, view: 'reports', subject: 'reports' },
];

/** Найти учебный предмет чтения; null -- в учебном контуре такого запроса нет */
export function matchRead(
	endpoint: string,
): { view: TrainingViewKind; subject: string; path: Record<string, string> } | null {
	for (const route of READ_ROUTES) {
		const match = route.pattern.exec(endpoint);
		if (!match) continue;
		const path: Record<string, string> = {};
		(route.path ?? []).forEach((name, index) => {
			path[name] = decodeURIComponent(match[index + 1]);
		});
		return { view: route.view, subject: route.subject, path };
	}
	return null;
}

/** Учебный экземпляр, через который идут запросы, и его текущая версия */
export interface TrainingSession {
	instanceId: string;
	version(): number;
	setVersion(version: number): void;
}

const WORKFLOW = /^\/v2\/log-events\/([^/]+)\/workflow$/;
const POLICY_SAVE = /^\/v2\/policies(?:\/([^/]+))?$/;

/** Тело рабочего сохранения политики доступа (AccessPolicyPayload) → учебный черновик */
function accessDraft(body: unknown): AccessPolicyDraftInput | null {
	if (!body || typeof body !== 'object') return null;
	const payload = body as {
		type?: string;
		name?: string;
		description?: string;
		is_active?: boolean;
		severity?: string | null;
		access?: {
			scope: AccessPolicyDraftInput['scope'];
			conditions: AccessPolicyDraftInput['conditions'];
		};
	};
	if (payload.type !== 'access' || !payload.access) return null;
	return {
		name: payload.name ?? '',
		description: payload.description ?? '',
		enabled: payload.is_active ?? false,
		severity: payload.severity ?? null,
		scope: payload.access.scope,
		conditions: payload.access.conditions,
	};
}

/** Команда без ID запроса: его назначает отправка */
type CommandBody =
	| Omit<Extract<TrainingCommand, { kind: 'event.assign' }>, 'request_id'>
	| Omit<Extract<TrainingCommand, { kind: 'policy.save' }>, 'request_id'>;

/**
 * Рабочая мутация → учебная команда; null -- в учебном контуре такого изменения нет.
 * Версия разбора в учебном событии -- версия экземпляра: разбор ожидает ту, что видел
 * пользователь; сохранение политики -- текущую версию экземпляра.
 */
export function toCommand(request: RoutedRequest, version: number): CommandBody | null {
	const workflow = request.method === 'PATCH' ? WORKFLOW.exec(request.endpoint) : null;
	if (workflow) {
		const patch = request.body as WorkflowPatch;
		return {
			kind: 'event.assign',
			expected_version: patch.expected_version,
			event_id: decodeURIComponent(workflow[1]),
			status: patch.status,
			assignee_id: patch.assignee_id,
		};
	}
	const policy = request.method === 'POST' ? POLICY_SAVE.exec(request.endpoint) : null;
	const draft = policy ? accessDraft(request.body) : null;
	if (policy && draft) {
		return {
			kind: 'policy.save',
			expected_version: version,
			id: policy[1] ? decodeURIComponent(policy[1]) : null,
			draft,
		};
	}
	return null;
}

/**
 * ID запроса команды выводится из экземпляра и тела команды (с ожидаемой версией): повтор той же
 * команды после потерянного ответа уходит с тем же ID и получает прежний ответ сервера, а не 409.
 * Формат -- UUID версии 8 (своя схема) из первых 16 байт SHA-256.
 */
export async function commandRequestId(instanceId: string, body: CommandBody): Promise<string> {
	const digest = new Uint8Array(
		await crypto.subtle.digest(
			'SHA-256',
			new TextEncoder().encode(`${instanceId}\n${JSON.stringify(body)}`),
		),
	);
	const bytes = digest.slice(0, 16);
	bytes[6] = (bytes[6] & 0x0f) | 0x80;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function command(session: TrainingSession, request: RoutedRequest): Promise<unknown> {
	const body = toCommand(request, session.version());
	if (body === null) throw trainingUnavailableError();
	try {
		const result = await sendTrainingCommand(session.instanceId, {
			...body,
			request_id: await commandRequestId(session.instanceId, body),
		});
		session.setVersion(result.version);
		return result.view.data;
	} catch (error) {
		/* Экземпляр изменён в другой вкладке: следующая попытка пойдёт с его версией */
		if (error instanceof ApiError && error.status === 409) {
			session.setVersion((await getTrainingInstance(session.instanceId)).version);
		}
		throw error;
	}
}

/** Ответ фасада, когда самого экземпляра больше нет (удалён, истёк или принадлежит другому) */
export const TRAINING_INSTANCE_GONE = 'training instance not found';

function instanceGone(error: unknown): boolean {
	return (
		error instanceof ApiError && error.status === 404 && error.message === TRAINING_INSTANCE_GONE
	);
}

/**
 * Маршрутизатор учебного режима для HTTP-клиента. Пропажа экземпляра во время работы
 * сообщается источнику (onGone): страница не уговаривает «попробовать ещё раз».
 */
export function createTrainingRouter(
	session: TrainingSession,
	onGone: () => void = () => {},
): RequestRouter {
	const route = routeOf(session);
	return async (request) => {
		try {
			return await route(request);
		} catch (error) {
			if (instanceGone(error)) onGone();
			throw error;
		}
	};
}

function routeOf(session: TrainingSession): RequestRouter {
	return async (request) => {
		if (request.method !== 'GET') return command(session, request);
		/* Фильтры приходят и параметрами запроса, и строкой в адресе (каталог: kinds=a&kinds=b) */
		const [path, query = ''] = request.endpoint.split('?');
		const search = new URLSearchParams(query);
		Object.entries(request.params ?? {}).forEach(([key, value]) => {
			if (value !== null && value !== undefined) search.set(key, String(value));
		});
		const diagnostic = demoDiagnosticRead(path, search);
		if (diagnostic !== undefined) return diagnostic;
		const read = matchRead(path);
		if (!read) throw trainingUnavailableError();
		search.set('lang', i18n.resolvedLanguage ?? i18n.language);
		Object.entries(read.path).forEach(([key, value]) => search.set(key, value));
		const view = await getTrainingView(session.instanceId, read.view, read.subject, search);
		return view.data;
	};
}

/** Учебный источник данных: свой кеш запросов и маршрутизатор в фасад экземпляра */
export function trainingDataSource(
	instanceId: string,
	version: number,
	onGone?: () => void,
): WorkspaceDataSource {
	let current = version;
	const session: TrainingSession = {
		instanceId,
		version: () => current,
		setVersion: (next) => {
			current = next;
		},
	};
	return {
		mode: { training_instance_id: instanceId },
		queryClient: createQueryClient(),
		router: createTrainingRouter(session, onGone),
	};
}
